import { useState } from 'react';
import type { ReactNode } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { RowActionButtons } from '@/components/tables/RowActionButtons';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { centsApi } from '@/api/services/cents';
import type { CentDto } from '@/api/types/cents';
import { ApiError } from '@/api/client/abpError';
import { CentFormModal } from './CentFormModal';
import type { CentFormValues } from './CentFormModal';
import { CentsPermissions } from './permissions';
import { useCreateCent, useDeleteCent, useUpdateCent } from './api';

// Add is inline (legacy adds a new editable row, not a modal); edit still uses the modal.
type ModalState = { mode: 'edit'; cent: CentDto } | null;

const EMPTY_DRAFT: CentFormValues = { name: '', relatedSymbol: '', contractSize: '' };

function toNumber(value: string, fallback: number): number {
  const n = Number(value.replace(/,/g, ''));
  return Number.isFinite(n) ? n : fallback;
}

export function CentsPage() {
  const can = usePermission();
  const canCreate = can(CentsPermissions.create);
  const canEdit = can(CentsPermissions.edit);
  const canDelete = can(CentsPermissions.delete);

  const toast = useToast();
  const createMut = useCreateCent();
  const updateMut = useUpdateCent();
  const deleteMut = useDeleteCent();

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<CentDto | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<CentFormValues>(EMPTY_DRAFT);

  const table = useServerDataTable<CentDto>({
    queryKey: ['cents', 'list'],
    fetcher: (req) => centsApi.getListForDataTable(req),
  });

  const columns: DataGridColumn<CentDto>[] = [
    { key: 'name', header: 'Name', sortable: true, cell: (r) => r.name },
    { key: 'relatedSymbol', header: 'Related Symbol', sortable: true, cell: (r) => r.relatedSymbol },
    { key: 'contractSize', header: 'Contract Size', sortable: true, cell: (r) => r.contractSize },
  ];

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  // Edit still uses the modal.
  function handleSubmit(values: CentFormValues) {
    if (!modal) return;
    updateMut.mutate(
      {
        id: modal.cent.id,
        input: {
          relatedSymbol: values.relatedSymbol.trim(),
          contractSize: toNumber(values.contractSize, modal.cent.contractSize),
        },
      },
      {
        onSuccess: () => {
          toast.success('Cent updated.');
          setModal(null);
        },
        onError: (err) => reportError(err, 'Failed to update the cent.'),
      },
    );
  }

  // Add is an inline editable row (legacy behaviour).
  function startAdd() {
    setDraft(EMPTY_DRAFT);
    setAdding(true);
  }

  function setField(key: keyof CentFormValues, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function saveAdd() {
    if (!draft.name.trim()) {
      toast.error('Please enter a name.');
      return;
    }
    if (!draft.relatedSymbol.trim()) {
      toast.error('Please enter a related symbol.');
      return;
    }
    if (!draft.contractSize.trim() || !Number.isFinite(Number(draft.contractSize.replace(/,/g, '')))) {
      toast.error('Please enter a valid contract size.');
      return;
    }
    createMut.mutate(
      {
        name: draft.name.trim(),
        relatedSymbol: draft.relatedSymbol.trim(),
        contractSize: toNumber(draft.contractSize, 0),
      },
      {
        onSuccess: () => {
          toast.success('Cent created.');
          setAdding(false);
        },
        onError: (err) => reportError(err, 'Failed to create the cent.'),
      },
    );
  }

  const textCell = (key: keyof CentFormValues) => (
    <input
      className="form-control form-control-sm"
      value={draft[key]}
      autoComplete="off"
      onChange={(e) => setField(key, e.target.value)}
    />
  );

  const createRow: Record<string, ReactNode> | undefined = adding
    ? {
        name: textCell('name'),
        relatedSymbol: textCell('relatedSymbol'),
        contractSize: textCell('contractSize'),
      }
    : undefined;

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Cent deleted.');
        setDeleteTarget(null);
      },
      onError: (err) => {
        reportError(err, 'Failed to delete the cent.');
        setDeleteTarget(null);
      },
    });
  }

  const showRowActions = canEdit || canDelete;

  return (
    <div>
      <PageHeader title="Cents" breadcrumb={['Home', 'Cents']} />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No cents found."
            toolbarEnd={
              canCreate && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={startAdd}
                  disabled={adding}
                >
                  <i className="fa fa-plus me-1" />
                  Create New
                </button>
              )
            }
            createRow={createRow}
            createRowActions={
              adding ? (
                <div className="d-inline-flex gap-1 justify-content-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    title="Save"
                    onClick={saveAdd}
                    disabled={createMut.isPending}
                  >
                    <i className="fa fa-save" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Cancel"
                    onClick={() => setAdding(false)}
                    disabled={createMut.isPending}
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>
              ) : undefined
            }
            rowActions={
              showRowActions || canCreate
                ? (row) =>
                    showRowActions ? (
                      <RowActionButtons
                        onEdit={canEdit ? () => setModal({ mode: 'edit', cent: row }) : undefined}
                        onDelete={canDelete ? () => setDeleteTarget(row) : undefined}
                      />
                    ) : null
                : undefined
            }
          />
        </div>
      </div>

      {modal && (
        <CentFormModal
          show
          mode={modal.mode}
          initial={modal.mode === 'edit' ? modal.cent : undefined}
          busy={createMut.isPending || updateMut.isPending}
          onSubmit={handleSubmit}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Delete cent"
        message={`Delete ${deleteTarget?.name ?? ''}? This cannot be undone.`}
        confirmLabel="Delete"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
